import { balances } from './ledger'
import { isKuurExpired } from './kuur'
import { toCents } from '../format/currency'
import type { Course } from '../types/account'
import type { LedgerEntry, LiabilitiesReport, VoucherStatus } from '../types/vouchers'

/**
 * Wat klanten al betaald hebben maar nog niet gekregen: de verplichtingen op de balans
 * (docs/BOEKHOUDING.md §5). Op één dag, dus een stand, geen periode.
 * - Cadeaubonnen: saldo van actieve én geblokkeerde bonnen (een blokkade heft de schuld niet op).
 *   Verlopen bonnen zijn vervallen (die regel staat in het grootboek).
 * - Vooruitbetaalde kuren: naar verhouding van de sessies die nog open staan (betaald × open ÷ totaal).
 *   Dat is het deel dat nog geen omzet is. Verlopen kuren tellen niet mee.
 * - Klanttegoed: euro's en behandelingen apart; behandelingen hebben geen vaste prijs.
 */

export type LiabilityInput = {
  vouchers: readonly { balanceCents: number; status: VoucherStatus }[]
  /** Kuren met wat er betaald is, in euro's (zoals `CoursePrepaid.paid`). */
  courses: readonly Course[]
  /** Per klant haar tegoedgrootboek. */
  credits: readonly (readonly LedgerEntry[])[]
}

const cents = (euro: number) => (Number.isFinite(euro) ? toCents(euro) : 0)

/** Het nog niet verdiende deel van één vooruitbetaalde kuur, in centen. */
export function openKuurCents(course: Course, today: string): number {
  const p = course.prepaid
  if (!p || isKuurExpired(course, today) || course.total <= 0) return 0
  const open = Math.max(0, course.total - Math.min(course.done, course.total))
  return Math.round((cents(p.paid) * open) / course.total)
}

export function liabilities(input: LiabilityInput, today: string): LiabilitiesReport {
  const open = input.vouchers.filter((v) => (v.status === 'actief' || v.status === 'geblokkeerd') && v.balanceCents > 0)
  const kuren = input.courses.map((c) => ({ c, cents: openKuurCents(c, today) })).filter((k) => k.cents > 0)
  const credit = input.credits.map((ledger) => balances([...ledger])).filter((b) => b.cents > 0 || b.treatment > 0)
  return {
    on: today,
    vouchers: { count: open.length, cents: open.reduce((s, v) => s + v.balanceCents, 0) },
    kuren: {
      count: kuren.length,
      sessions: kuren.reduce((s, k) => s + Math.max(0, k.c.total - k.c.done), 0),
      cents: kuren.reduce((s, k) => s + k.cents, 0),
    },
    credit: {
      customers: credit.length,
      cents: credit.reduce((s, b) => s + Math.max(0, b.cents), 0),
      treatments: credit.reduce((s, b) => s + Math.max(0, b.treatment), 0),
    },
  }
}
